import { Dispatch } from 'react';
import { useIntl } from 'react-intl';

import { setError, startSpinner, stopSpinner } from '@actions/common/actionCreators';
import { TICKET_LIST_ACTION_TYPES } from '@constants/actionTypes/tikcetListActionTypes';
import { API_STATUS_CODES } from '@constants/common.constants';
import { SPINNER_MESSAGES_IDS } from '@constants/common/miscellaneous';
import { configs } from '@constants/configs/configs';
import { api } from '@modules/common-utils-services/axiosUtil';
import API from '@services/API';
import { TicketApiType } from '../constants/ticketlist.constants';
import { RequestStatus } from '../constants/ticketlist.interface';

const actions = { setError, startSpinner, stopSpinner };

export const responseCheck = response => {
    if (
        response?.message?.toLowerCase() === RequestStatus.SUCCESS ||
        response === RequestStatus.SUCCESS ||
        response !== '' ||
        response !== null ||
        response.statusCode === API_STATUS_CODES.OK
    ) {
        return true;
    }
    return false;
};

export const handleApiResponse = async (
    apiCall: Function,
    payload: any,
    dispatch: Dispatch<any>,
    onSuccess?: Function,
    onError?: Function,
) => {
    try {
        const response = await apiCall(dispatch, payload);
        const { data, statusCode, statusMessage } = response || {};
        if (response && responseCheck(response) && onSuccess) {
            onSuccess(data);
        } else if (onError) {
            onError(statusMessage || response);
        }
    } catch (error) {
        console.error('API Error:', error);
        if (onError) {
            onError(error);
        }
    }
};

const apiCall = async (
    dispatch: Dispatch<any>,
    url: any,
    payload: any,
    type: string = 'GET',
    showSpinner: boolean = true,
) => {
    try {
        if (type === 'GET') {
            const newUrl = url.processReplacements(payload);
            const response = await API.getData(newUrl, dispatch, actions, showSpinner);
            return response;
        } else {
            showSpinner && dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const body = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                body.append(key, value as string);
            });
            const response = await api.post(url, body);
            showSpinner && dispatch(stopSpinner());
            return response.data;
        }
    } catch (error) {
        showSpinner && dispatch(stopSpinner());
        throw new Error(error?.response?.data?.statusMessage);
    }
};

export const useApiHandler = () => {
    const intl = useIntl();
    const controllerAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const url = configs.endpoints.TICKETS_LIST_TICKET_CONTROLLER;
            const response = await apiCall(dispatch, url, payload, 'GET', false);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_DEFAULT_CONTROLLER, payload: response.data });
            dispatch(stopSpinner());
            return response;
        } catch (error) {
            console.error('Error in controllerAPI:', error);
        }
    };

    const defaultListAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_DEFAULT_LIST;
            const response = await apiCall(dispatch, url, payload, 'POST');
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKETS_LIST, payload: response.data });
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_LIST_API_TYPE, payload: TicketApiType.DEFAULTLIST });
            return response;
        } catch (error) {
            console.error('Error in defaultListAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchTickets' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage: errorMessage,
                    modalTitle: 'ticketList.modal.errorModal.header.notFound',
                },
            });
        }
    };

    const filteredListAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_FILTERED_LIST;
            const response = await apiCall(dispatch, url, payload, 'POST');
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKETS_LIST, payload: response.data });
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_LIST_API_TYPE, payload: TicketApiType.FILTEREDLIST });
            return response;
        } catch (error) {
            console.error('Error in filteredListAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchTickets' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage: errorMessage,
                    modalTitle: 'ticketList.modal.errorModal.header.notFound',
                },
            });
        }
    };

    const listAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST;
            const response = await apiCall(dispatch, url, payload, 'POST');
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKETS_LIST, payload: response.data });
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_LIST_API_TYPE, payload: TicketApiType.LIST });
            return response;
        } catch (error) {
            console.error('Error in listAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchTickets' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage: errorMessage,
                    modalTitle: 'ticketList.modal.errorModal.header.notFound',
                },
            });
        }
    };

    const claimNewTicketAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_CLAIM_NEW_TICKET;
            const response = await apiCall(dispatch, url, payload, 'POST');
            return response;
        } catch (error) {
            let errorMessage = error.toString();
            let modalTitle = '';
            if (
                errorMessage.indexOf(intl.formatMessage({ id: 'ticketList.errors.claimTicket.internalError' })) !== -1
            ) {
                errorMessage = intl.formatMessage({ id: 'ticketList.errors.claimTicket.internalErrorMessage' });
                modalTitle = 'ticketList.modal.errorModal.header.noAccess';
            }
            if (errorMessage.indexOf(intl.formatMessage({ id: 'ticketList.errors.claimTicket.noUnclaimed' })) !== -1) {
                errorMessage = intl.formatMessage({ id: 'ticketList.errors.claimTicket.noUnclaimedMessage' });
                modalTitle = 'ticketList.modal.errorModal.header.notFound';
            }
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage, modalTitle },
            });
        }
    };

    const ticketAttachmentsApi = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_ATTACHMENTS;
            const response = await apiCall(dispatch, url, payload);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_ATTACHMENTS, payload: response });
            return response;
        } catch (error) {
            console.error('Error in ticketAttachmentsApi:', error);
        }
    };

    const ticketActiveTaskAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_TICKET_ACTIVE_TASKS;
            const response = await apiCall(dispatch, url, payload);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_ACTIVE_TASK_DATA, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in ticketActiveTaskAPI:', error);
            throw new Error(error);
        }
    };

    const userTicketTaskListAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_USER_TICKET_TASK_LIST;
            const response = await apiCall(dispatch, url, payload, 'POST');
            return response;
        } catch (error) {
            console.error('Error in ticketActiveTaskAPI:', error);
            throw new Error(error);
        }
    };

    const encTaskAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_ENC_VALUE;
            const response = await apiCall(dispatch, url, payload);
            return response;
        } catch (error) {
            console.error('Error in encTaskAPI:', error);
            throw new Error(error);
        }
    };

    const claimTaskAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_CLAIM_USER_TASK;
            const response = await apiCall(dispatch, url, payload, 'POST');
            return response;
        } catch (error) {
            console.error('Error in claimTaskAPI:', error);
            let errorMessage = error.toString();
            let modalTitle = 'ticketList.modal.errorModal.header.error';
            if (errorMessage.indexOf('Error::') !== -1) {
                errorMessage = intl.formatMessage({ id: 'ticketList.errors.claimTaskNoAccess' });
                modalTitle = 'ticketList.modal.errorModal.header.noAccess';
            } else {
                errorMessage = intl.formatMessage({ id: 'ticketList.errors.claimFailed' });
            }

            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage,
                    modalTitle,
                },
            });
        }
    };

    const getTaskFromPropertiesAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_TASK_FROM_PROPERTIES;
            const response = await apiCall(dispatch, url, payload, 'POST');
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_TICKET_ACTIVE_TASK_FROM_PROPERTIES_LIST,
                payload: response.data,
            });
            return response;
        } catch (error) {
            console.error('Error in completeTaskAPI:', error);
            throw new Error(error);
        }
    };

    const fetchDocumentsAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_CLAIM_USER_TASK;
            const response = await apiCall(dispatch, url, payload, 'POST');
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_TICKET_DOCUMENTS,
                payload: response.data,
            });
            return response;
        } catch (error) {
            console.error('Error in fetchDocumentsAPI:', error);
        }
    };

    const cancelTicketAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_CANCEL_TICKET;
            const response = await apiCall(dispatch, url, payload, 'POST');
            return response;
        } catch (error) {
            console.error('Error in cancelTicketAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.cancelFailed' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage: errorMessage,
                    modalTitle: 'ticketList.modal.errorModal.header.error',
                },
            });
        }
    };

    const completeUserTaskAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_COMPLETE_USER_TASK;
            const response = await apiCall(dispatch, url, payload, 'POST');
            return response;
        } catch (error) {
            console.error('Error in completeUserTaskAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.completeTicket' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage: errorMessage,
                    modalTitle: 'ticketList.modal.errorModal.header.error',
                },
            });
        }
    };

    const reopenTicketAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_REOPEN_TICKET;
            const response = await apiCall(dispatch, url, payload, 'POST');
            return response;
        } catch (error) {
            console.error('Error in reopenTicketAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.reopenFailed' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const ticketDetailAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_TICKET_DETAIL;
            const response = await apiCall(dispatch, url, payload);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_DETAILS, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in ticketDetailAPI:', error);
        }
    };

    const allUsersForQueueAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_ALL_USERS_FOR_QUEUE;
            const response = await apiCall(dispatch, url, payload);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_ALL_USERS_FOR_QUEUE, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in allUsersForQueueAPI:', error);
            throw new Error(error);
        }
    };

    const reassignTicketAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_REASSIGN_TICKET;
            const response = await apiCall(dispatch, url, payload);
            return response;
        } catch (error) {
            console.error('Error in reassignTicketAPI:', error);
        }
    };

    const bulkReassignTicketAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_BULK_REASSIGN_TICKETS;
            const response = await apiCall(dispatch, url, payload);
            return response;
        } catch (error) {
            console.error('Error in bulkReassignTicketAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.bulkReassignFailed' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const bulkReassignmentUsersAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_BULK_ASSIGNMENT_USERS;
            const response = await apiCall(dispatch, url, payload);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_BULK_ASSIGNMENT_USERS, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in bulkReassignmentUsersAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.bulkReassignNoActiveUsers' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const ticketSubjectAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_TICKET_SUBJECT;
            const response = await apiCall(dispatch, url, payload, 'POST', false);
            return response;
        } catch (error) {
            console.error('Error in ticketSubjectAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchSubject' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const ticketAssigneeAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_ASSIGNEE_LIST;
            const response = await apiCall(dispatch, url, payload, 'POST', false);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_ASSIGNEE, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in ticketAssigneeAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchAssignee' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const ticketRequestorsAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_TICKET_REQUESTORS;
            const response = await apiCall(dispatch, url, payload, 'POST', false);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_REQUESTERS, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in ticketRequestorsAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchRequestor' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const createdForAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_CREATED_FOR;
            const response = await apiCall(dispatch, url, payload, 'POST', false);
            return response;
        } catch (error) {
            console.error('Error in createdForAPI:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedFetchCreatedFor' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: { isOpen: true, errorMessage: errorMessage },
            });
        }
    };

    const findByCategoryAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_FIND_BY_CATEGORY;
            const response = await apiCall(dispatch, url, payload, 'GET', false);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_CATEGORY, payload: response });
            return response;
        } catch (error) {
            console.error('Error in findByCategoryAPI:', error);
        }
    };

    const customFieldOptionsAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_CUSTOM_FIELD_V2;
            const response = await apiCall(dispatch, url, payload, 'GET', false);
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_CUSTOM_FIELD_OPTIONS, payload: response.data });
            return response;
        } catch (error) {
            console.error('Error in findByCategoryAPI:', error);
        }
    };

    const exportTicketListAPI = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_EXPORT_LIST.processReplacements(payload);
            const response = await API.downloadFile(url, dispatch, actions);
            return response;
        } catch (error) {
            console.error('Error in exportTicketListAPI:', error);
        }
    };

    const findWorkgroupByTagName = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_FETCH_WORKGROUP_BY_TAG_NAME;
            const response = await apiCall(dispatch, url, payload, 'GET', false);
            return response;
        } catch (error) {
            console.error('Error in findWorkgroupByTagName:', error);
        }
    };

    const updateWatchersApi = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_UPDATE_WATCHER;
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = await api.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'csrf-token': window.csrf_token || '',
                },
            });
            dispatch(stopSpinner());
            return response;
        } catch (error) {
            dispatch(stopSpinner());
            console.error('Error in updateWatchersApi:', error);
            const errorMessage = intl.formatMessage({ id: 'ticketList.errors.failedUpdateWatchers' });
            dispatch({
                type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
                payload: {
                    isOpen: true,
                    errorMessage: errorMessage,
                    modalTitle: 'ticketList.modal.errorModal.header.error',
                },
            });
        }
    };

    const findWacthers = async (dispatch: Dispatch<any>, payload: any) => {
        try {
            const url = configs.endpoints.TICKETS_LIST_WATCHERS;
            const response = await apiCall(dispatch, url, payload, 'GET', false);
            return response;
        } catch (error) {
            console.error('Error in findWacthers:', error);
        }
    };

    return {
        controllerAPI,
        defaultListAPI,
        filteredListAPI,
        listAPI,
        claimNewTicketAPI,
        ticketAttachmentsApi,
        ticketActiveTaskAPI,
        encTaskAPI,
        claimTaskAPI,
        fetchDocumentsAPI,
        cancelTicketAPI,
        completeUserTaskAPI,
        reopenTicketAPI,
        ticketDetailAPI,
        allUsersForQueueAPI,
        reassignTicketAPI,
        getTaskFromPropertiesAPI,
        bulkReassignTicketAPI,
        bulkReassignmentUsersAPI,
        ticketSubjectAPI,
        ticketAssigneeAPI,
        ticketRequestorsAPI,
        createdForAPI,
        findByCategoryAPI,
        userTicketTaskListAPI,
        customFieldOptionsAPI,
        exportTicketListAPI,
        findWorkgroupByTagName,
        updateWatchersApi,
        findWacthers,
    };
};
